/**
 * TrendChart Component
 * 
 * Base Victory chart component with Meridian theming for displaying
 * measurement trend data with nominal and baseline series.
 */

import React from 'react';
import {
  VictoryChart,
  VictoryVoronoiContainer,
  VictoryAxis,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import Legend, { LegendProvider } from '@amzn/meridian/legend';
import Heading from '@amzn/meridian/heading';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import victoryLinePlugin from '@amzn/meridian/legend/plugin/victory-line';
import victoryTooltipPlugin from '@amzn/meridian/legend/plugin/victory-tooltip';
import useVictoryTheme from '@amzn/meridian/use-victory-theme';
import { TrendChartData } from '../../../services/equipment/types';
import { useVictoryZoom } from '../../../hooks/use-victory-zoom';
import { CustomColors, PulseCustomColors } from './technology-chart';

export interface TrendChartProps {
  /** Chart data including nominal and baseline series */
  data: TrendChartData;
  /** Chart width in pixels */
  width?: number;
  /** Chart height in pixels */
  height?: number;
  /** Whether to show independent grid lines */
  showIndependentGrid?: boolean;
  /** Whether to show crosshair on hover */
  showCrosshair?: boolean;
  /** Custom chart title (defaults to technology name) */
  title?: string;
  /** Optional click handler for data points */
  onDataPointClick?: (datum: any) => void;
  /** Whether to enable data point clicks */
  enableDataPointClicks?: boolean;
  /** Size of scatter plot dots in pixels */
  dotSize?: number;
  /** Whether to enable zoom functionality (default: true) */
  enableZoom?: boolean;
  /** Maximum data points to render for performance (default: 1000) */
  maxDataPoints?: number;
  /** Minimum zoom level (default: 0.1 for 10x zoom in) */
  minimumZoom?: number;
  /** Maximum zoom level (default: 10 for 10x zoom out) */
  maximumZoom?: number;
  /** Custom colors for chart lines */
  customColors?: CustomColors;
  /** Pulse-specific custom colors (only applies to Pulse charts) */
  pulseColors?: PulseCustomColors;
}

/**
 * Formats tooltip labels
 */
const formatLabel = ({ datum }: { datum: any }) => {
  return `${datum.y}`;
};

/**
 * Formats axis tick values
 */
const formatTimestamp = (timestamp: any) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatValue = (value: number, index: number) => {
  // Don't show label for first tick to avoid crowding
  return index > 0 ? value.toString() : '';
};

/**
 * Gets display name for technology type
 */
const getTechnologyDisplayName = (technology: string): string => {
  const technologyMap: Record<string, string> = {
    monitron: 'Monitron (Vibration and Temperature)',
    pulse: 'PULSE (ULTS)',
    cadet: 'CADET (Current)',
    oil: 'Oil Sampling (Particle count)',
    thermal: 'ASTRA (Thermal)',
  };
  
  return technologyMap[technology] || technology;
};

/**
 * Helper function to get custom color for a data series key
 */
const getCustomColor = (
  key: string, 
  technology: string, 
  pulseColors?: PulseCustomColors, 
  customColors?: CustomColors
): string | undefined => {
  // For Pulse charts, try Pulse-specific colors first
  if (technology === 'pulse' && pulseColors) {
    switch (key) {
      case 'dB':
      case 'db':
        return pulseColors.dB;
      case 'baseline':
        return pulseColors.baseline;
      case 'lubrication':
        return pulseColors.lubrication;
      case 'startOfFailure':
        return pulseColors.startOfFailure;
      case 'criticalFailure':
        return pulseColors.criticalFailure;
    }
  }
  
  // Fallback to generic custom colors
  if (customColors) {
    if (key === 'nominal' || key === 'db' || key.toLowerCase().includes('nominal')) {
      return customColors.nominal;
    } else if (key === 'baseline' || key.toLowerCase().includes('baseline')) {
      return customColors.baseline;
    } else if (key === 'temperature' || key.toLowerCase().includes('temperature')) {
      return customColors.temperature;
    }
  }
  
  return undefined; // Use default colors
};

/**
 * TrendChart component that displays measurement data with Victory and Meridian integration
 */
export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  width = 600,
  height = 260,
  showIndependentGrid = false,
  showCrosshair = true,
  title,
  onDataPointClick,
  enableDataPointClicks = false,
  dotSize = 3,
  enableZoom = true,
  maxDataPoints = 1000,
  minimumZoom = 0.1,
  maximumZoom = 10,
  customColors,
  pulseColors,
}) => {
  const theme = useVictoryTheme({ showIndependentGrid });
  
  // Create datasets for legend integration with custom colors
  const datasets = data.timeSeries;
  const chartTitle = title || getTechnologyDisplayName(data.technology);
  const chartName = `trend-chart-${data.technology}`;

  // Use the reusable zoom hook
  const { zoomContainer, zoomDisplayText } = useVictoryZoom({
    enableZoom,
    minimumZoom,
    allowPan: true,
    allowZoom: true,
  });

  // Apply custom colors to datasets for legend synchronization
  const processedDatasets = datasets.map((dataset) => {
    const customColor = getCustomColor(dataset.key, data.technology, pulseColors, customColors);
    return {
      ...dataset,
      // Add color information for legend
      color: customColor
    };
  });

  // Create color scale for custom colors if they exist
  const hasCustomColors = processedDatasets.some(d => d.color);
  const colorScale = hasCustomColors 
    ? processedDatasets.map(d => d.color).filter(Boolean)
    : undefined;

  return (
    <LegendProvider
      data={processedDatasets}
      plugins={[
        victoryLinePlugin({ 
          theme,
          // Override colors if we have custom colors
          ...(hasCustomColors && { colorScale })
        }),
        victoryTooltipPlugin({ showCrosshair }),
      ]}
      aria-label={`Line chart showing ${chartTitle} measurements over time`}
    >
      {({ getLineProps, getScatterProps, voronoiContainerProps, Tooltip }) => {
        // Choose appropriate container component
        const containerComponent = (() => {
          // Use zoom container from hook if zoom enabled
          if (enableZoom && zoomContainer) {
            return zoomContainer;
          }

          // If zoom disabled but clicks enabled, no container needed
          if (enableDataPointClicks) {
            return undefined;
          }

          // Default: use tooltip container
          return (
            <VictoryVoronoiContainer
              labels={formatLabel}
              {...voronoiContainerProps}
            />
          );
        })();

        return (
        <div 
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            transition: 'transform 200ms ease-in-out, box-shadow 200ms ease-in-out',
            transformOrigin: 'center',
            cursor: 'default'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
          }}
        >
          <Column spacing="200">
            {/* Chart Header with Zoom Info */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Heading level={4}>{chartTitle}</Heading>
              {enableZoom && (
                <Text type="b200" color="secondary">
                  {zoomDisplayText}
                </Text>
              )}
            </div>

            <VictoryChart
              theme={theme}
              width={width}
              height={height}
              name={chartName}
              // domain={entireDomain}
              containerComponent={containerComponent}
            >
              <VictoryAxis 
                tickFormat={(x: any) => {
                  const date = new Date(x);
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                }}
                fixLabelOverlap={true}
                style={{
                  axis: { stroke: "#dee2e6", strokeWidth: 1 },
                  tickLabels: { 
                    fontSize: 11, 
                    fill: "#333333", 
                    fontWeight: 500
                  },
                  grid: { stroke: "#f8f9fa", strokeWidth: 1 }
                }}
              />
              <VictoryAxis
                dependentAxis
                tickFormat={formatValue}
                style={{
                  axis: { stroke: "#dee2e6", strokeWidth: 1 },
                  tickLabels: { 
                    fill: "#333333", 
                    fontSize: 12, 
                    fontWeight: 500
                  },
                  grid: { stroke: "#e9ecef", strokeWidth: 1, strokeDasharray: "2,2" }
                }}
              />
              
              {/* Render lines for each dataset */}
              {processedDatasets.map(({ data: chartData, key }) => {
                const lineProps = getLineProps(key);
                const customColor = getCustomColor(key, data.technology, pulseColors, customColors);

                // Only apply custom style if we have a custom color
                if (customColor) {
                  const customStyle = {
                    data: {
                      strokeWidth: 2,
                      strokeLinecap: "round",
                      stroke: customColor
                    }
                  };

                  return (
                    <VictoryLine
                      key={key}
                      data={chartData}
                      style={customStyle}
                      {...lineProps}
                    />
                  );
                } else {
                  // Use theme colors as-is when no custom color
                  return (
                    <VictoryLine
                      key={key}
                      data={chartData}
                      {...lineProps}
                    />
                  );
                }
              })}
              
              {/* Render scatter points for each dataset */}
              {processedDatasets.map(({ data: chartData, key }) => {
                // For PULSE technology: only render dots for 'db' line, skip others
                if (data.technology === 'pulse' && key !== 'db') {
                  return null;
                }
                
                const scatterProps = getScatterProps(key);
                const customColor = getCustomColor(key, data.technology, pulseColors, customColors);
                
                const clickEvents = enableDataPointClicks && onDataPointClick ? [{
                  target: "data",
                  eventHandlers: {
                    onClick: (event: any, props: any) => {
                      console.log('VictoryScatter clicked!', props.datum);
                      onDataPointClick(props.datum);
                      return [];
                    }
                  }
                }] : undefined;
                
                // Only override style if we have custom color
                if (customColor) {
                  return (
                    <VictoryScatter 
                      data={chartData} 
                      key={`${key}-scatter`}
                      {...scatterProps}
                      size={enableDataPointClicks ? Math.max(dotSize + 1, 4) : dotSize}
                      events={clickEvents}
                      style={{
                        ...scatterProps.style,
                        data: { 
                          ...scatterProps.style?.data,
                          fill: customColor,
                          stroke: customColor,
                          strokeWidth: 0,
                          cursor: enableDataPointClicks ? 'pointer' : 'default'
                        }
                      }}
                    />
                  );
                } else {
                  // Use theme colors as-is when no custom color
                  return (
                    <VictoryScatter 
                      data={chartData} 
                      key={`${key}-scatter`}
                      {...scatterProps}
                      size={enableDataPointClicks ? Math.max(dotSize + 1, 4) : dotSize}
                      events={clickEvents}
                      style={enableDataPointClicks ? {
                        ...scatterProps.style,
                        data: {
                          ...scatterProps.style?.data,
                          cursor: 'pointer'
                        }
                      } : scatterProps.style}
                    />
                  );
                }
              })}
            </VictoryChart>
            <Legend direction="horizontal" />
          </Column>
        </div>
        );
      }}
    </LegendProvider>
  );
};
